import { Dispatch, SetStateAction, useRef, useState } from "react"
import useSWRImmutable from "swr/immutable"

import {
    getEmptyHpDeviceFormData,
    mapHpDeviceResponseToDeviceFormData,
} from "../../../../../components/devices/lct/formSpecs/hpSpec"
import { HpDeviceFormData } from "../../../../../components/devices/lct/types/providerTypes"
import { LCTOnlyDatasets } from "../../../../../constants/enums"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import { getTsIoMapper } from "../../../../../utils/general/generalUtils"
import { getClientProtected } from "../../../clients/clients"
import { NEW_VERSION_CONFIG } from "../../../swrConfig"
import { NewHpDeviceVersionResponse } from "../../../types/devices/lct/publishHpDeviceTypes"
import { START_NEW_LCT_DEVICE_VERSION } from "../../../urls/private"

export interface IUseStartNewHpDeviceVersionOutput {
    data: HpDeviceFormData
    setData: Dispatch<SetStateAction<HpDeviceFormData>>
    isLoading: boolean
    apiResSystemRef: string | null
    isValidating: boolean
    error?: AppError
}

const startNewHpDeviceVersionMapper = getTsIoMapper(
    NewHpDeviceVersionResponse,
    "startNewHpDeviceVersionMapper"
)

/**
 * Hook for starting a new HP/EB device version.
 * Returns data for the new device version, which is then used to populate the device context
 */
const useStartNewHpDeviceVersion = (
    deviceRef: string,
    attachmentUUID: string,
    dataset: LCTOnlyDatasets = LCTOnlyDatasets.HP
): IUseStartNewHpDeviceVersionOutput => {
    const [data, setData] = useState<HpDeviceFormData>(
        getEmptyHpDeviceFormData()
    )
    const [isLoading, setIsLoading] = useState(true)

    // Only to be set in the SWR fetcher. Ref used as useState triggers the
    // re-render
    const apiResSystemRef = useRef<string | null>(null)

    const { error, isValidating } = useSWRImmutable(
        START_NEW_LCT_DEVICE_VERSION(deviceRef, attachmentUUID, dataset),
        async (url: string): Promise<void> => {
            try {
                const response = await getClientProtected(url)
                const mappedRes = startNewHpDeviceVersionMapper.map(response)
                apiResSystemRef.current = mappedRes.SystemReference
                setData(mapHpDeviceResponseToDeviceFormData(mappedRes))
            } catch {
                throw invalidApiDataError(
                    `Failed to start new ${dataset} version - data load failed`
                )
            } finally {
                setIsLoading(false)
            }
        },
        NEW_VERSION_CONFIG
    )

    return {
        data,
        setData,
        apiResSystemRef: apiResSystemRef.current,
        isLoading,
        isValidating,
        error,
    }
}

export default useStartNewHpDeviceVersion
